import { useState, useEffect } from 'react';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import { useNavigate, useLocation } from 'react-router-dom';
import { isAxiosError } from 'axios';
import logo from '../../../assets/yapa-logo.png';
import InfoSegment from '../InfoSegment/InfoSegment';
import MemberSegment from '../MemberSegment/MemberSegment';
import AttachmentSegment from '../AttachmentSegment/AttachmentSegment';
import { IInfoInput } from '../InfoSegment/InfoSegment';
import { IMemberWrapper } from '../MemberSegment/MemberSegment';
import './StarsAdding.css';

interface IMemberDB {
  id: number;
  firstName: string;
  lastName: string;
  instrument: string;
  microphone: string;
  phone: string;
}

export interface IAttachmentDB {
  picture: string;
  position: string;
}

const StarsPage = () => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const location = useLocation();
  const starId = sessionStorage.getItem('starId');
  const INFO_URL = starId ? `admin/bands/${starId}` : '/bands';
  const MEMBER_URL =  starId ? `admin/bandMembers/${starId}` : '/bandMembers';
  const ATTACHMENT_URL = starId ? `admin/images/${starId}` : '/images';

  const [info, setInfo] = useState<IInfoInput>({
    name: '',
    mail: ''
  });
  const [members, setMembers] = useState<IMemberWrapper[]>([]);
  const [attachment, setAttachment] = useState<IAttachmentDB>({
    picture: '',
    position: '',
  });

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
  
    const getInfo = async () => {
      try {
        const response = await axiosPrivate.get(INFO_URL, {
          signal: controller.signal,
        });
        const { name, email } = response.data;
  
        if (isMounted) {
          setInfo({
            name: name ?? '',
            mail: email ?? '',
          });
        }
      } catch (error) {
        if (isAxiosError(error)) {
          if (error.response && error.response.status === 403) {
            navigate('/', { state: { from: location }, replace: true });
          }
        }
      }
    };
  
    const getMembers = async () => {
      try {
        const response = await axiosPrivate.get(MEMBER_URL, {
          signal: controller.signal,
        });
  
        response.data.forEach((m: IMemberDB) =>
          setMembers((prev) => [
            ...prev,
            {
              id: m.id,
              inDB: true,
              action: 'none',
              input: {
                firstName: m.firstName,
                lastName: m.lastName,
                instrument: m.instrument,
                microphone: m.microphone,
                phone: m.phone,
              },
            },
          ])
        );
      } catch (error) {
        if (isAxiosError(error)) {
          if (error.response && error.response.status === 403) {
            navigate('/', { state: { from: location }, replace: true });
          }
        }
      }
    };
  
    const getAttachments = async () => {
      try {
        const response = await axiosPrivate.get(ATTACHMENT_URL, {
          signal: controller.signal,
        });
  
        setAttachment({
          position: response.data?.setting,
          picture: response.data?.band,
        });
      } catch (error) {
        if (isAxiosError(error)) {
          if (error.response && error.response.status === 403) {
            navigate('/', { state: { from: location }, replace: true });
          }
        }
      }
    };
  
    getInfo();
    getMembers();
    getAttachments();
  
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [INFO_URL, MEMBER_URL, ATTACHMENT_URL, axiosPrivate, navigate, location]);  // Add dependencies here
  

  return (
    <div className="band-container">
    <img src={logo} width={200}alt="Logo YAPA" />
      <div className="band-content">
        <InfoSegment data={info} />
        <MemberSegment data={members}/>
        <AttachmentSegment data={attachment} />
      </div>
    </div>
  );
};

export default StarsPage;
